import type { PropsWithChildren } from 'react';
import { Children } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { Marquee } from 'atoms/container/Marquee';
import { Container, FullWidth } from 'atoms/layout/Container';
import { Vertical } from 'atoms/layout/flex';
import { BodyM } from 'atoms/typography';
import type { League, Pictures } from 'components/landing/types';
import { useConnectionContext } from 'contexts/connection';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { useEvents } from 'lib/events/useEvents';
import { laptopAndAbove } from 'style/mediaQuery';

const TextContainer = styled(Vertical)`
  margin-top: var(--triple-unit);
  margin-left: var(--triple-unit);
  align-self: flex-start;
  padding: var(--double-unit);
  @media (${laptopAndAbove}) {
    width: 150%;
    align-self: center;
    margin-top: -50px;
  }
`;

const TitleText = styled.h2`
  font-size: var(--fs-landing-title);
  line-height: 1;
  font-family: var(--brand-font);
  text-transform: uppercase;
  text-wrap: balance;
`;

const StyledImage = styled.img`
  object-fit: contain;
  object-position: bottom;
  width: 100%;
  height: 200px;
  @media (${laptopAndAbove}) {
    position: absolute;
    z-index: -1;
    right: -5%;
    bottom: 0;
    height: 480px;
    width: auto;
    min-width: 0;
  }
`;

const ButtonWrapper = styled(Vertical)`
  z-index: 5;
  align-self: flex-start;
`;

const LeagueImageWrapper = styled(ButtonBase)`
  border-radius: 20px;
  overflow: hidden;
  width: 150px;

  @media (${laptopAndAbove}) {
    width: 300px;
  }
`;

const InnerWrapper = styled.div`
  width: 100%;
  border-radius: var(--quadruple-unit) var(--quadruple-unit) 0 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-image: var(--background);
  background-size: cover;
  mask-image: linear-gradient(to bottom, black 80%, transparent);
  @media (${laptopAndAbove}) {
    display: grid;
    grid-template-columns: 360px 1fr;
    flex-direction: row;
    position: relative;
    height: 588px;
  }
`;

const StyledFullWidth = styled(FullWidth)`
  margin-top: calc(var(--unit) * -2);
  @media (${laptopAndAbove}) {
    margin-top: -50px;
  }
`;

type Props = {
  assets: {
    background: Pictures;
    players: Pictures;
  };
  league: League;
  messages: {
    scout: MessageDescriptor;
    scoutDescription: MessageDescriptor;
    scoutButton: MessageDescriptor;
  };
};

export const Scout = ({
  assets,
  children,
  league,
  messages,
}: PropsWithChildren<Props>) => {
  const isLaptop = useIsLaptop();
  const { signUp } = useConnectionContext();
  const track = useEvents();

  const onClick = () => {
    track('Click Landing Page Scout', { clickSource: league });
    signUp();
  };

  return (
    <Container>
      <InnerWrapper
        style={
          {
            '--background': `image-set(url(${assets.background.avif}) type('image/avif'), url(${assets.background.pictureUrl}) type('image/png'))`,
          } as CSSProperties
        }
      >
        <TextContainer gap={3}>
          <TitleText>
            <FormattedMessage {...messages.scout} />
          </TitleText>
          <BodyM text-center>
            <FormattedMessage {...messages.scoutDescription} />
          </BodyM>
          <ButtonWrapper>
            <Button color="secondary" size="large" onClick={onClick}>
              <FormattedMessage {...messages.scoutButton} />
            </Button>
          </ButtonWrapper>
        </TextContainer>
        <picture>
          <source srcSet={assets.players.avif} type="image/avif" />
          <StyledImage
            src={assets.players.pictureUrl}
            alt=""
            height={isLaptop ? '480' : '300'}
          />
        </picture>
      </InnerWrapper>
      <StyledFullWidth>
        <Marquee direction="reverse">
          {Children.toArray(children).map((child, index) => (
            <LeagueImageWrapper
              type="button"
              onClick={signUp}
              // eslint-disable-next-line react/no-array-index-key
              key={index}
            >
              {child}
            </LeagueImageWrapper>
          ))}
        </Marquee>
      </StyledFullWidth>
    </Container>
  );
};
